import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/api-client';

export interface BookingDetail {
  id: string;
  bidId: string | null;
  bidProjectId: string | null;
  projectId: string | null;  // client_project_id for timeline editing
  title: string;
  date: string;
  startTime: string;
  endTime: string;
  location: string;
  client: {
    clientId: string;
    name: string;
    email: string;
    phone: string;
    company: string;
    companyDescription?: string;
    industry?: string;
    logoUrl?: string;
    avatar?: string;
  };
  description: string;
  status: 'upcoming' | 'in-progress' | 'completed' | 'cancelled';
  timeline: {
    id: string;
    title: string;
    description: string;
    date: string;
    icon: string;
    status: 'completed' | 'current' | 'upcoming';
  }[];
  deliverables: {
    id: string;
    title: string;
    description: string;
    dueDate: string;
    status: 'not-started' | 'in-progress' | 'completed' | 'overdue';
  }[];
  budget: number;
  paymentMode?: 'platform' | 'off_platform';
}

// Helper function to determine timeline event status
const determineTimelineEventStatus = (eventDate: Date): 'completed' | 'current' | 'upcoming' => {
  const now = new Date();
  if (eventDate < now) return 'completed';
  if (isSameDay(eventDate, now)) return 'current';
  return 'upcoming';
};

// Helper function to check if two dates are the same day
const isSameDay = (date1: Date, date2: Date) => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

// Helper function to map job status
const mapJobStatus = (
  status: string | null,
  eventDate: Date
): BookingDetail['status'] => {
  if (status === 'completed') return 'completed';
  if (status === 'cancelled') return 'cancelled';

  const now = new Date();
  if (eventDate < now) return 'in-progress';
  return 'upcoming';
};

// Helper function to map deliverable status
const mapDeliverableStatus = (
  status: string | null | undefined,
  dueDate: Date
): 'completed' | 'in-progress' | 'not-started' | 'overdue' => {
  if (status === 'Approved' || status === 'approved') return 'completed';
  if (status === 'Submitted' || status === 'submitted') return 'in-progress';

  const now = new Date();
  if (dueDate < now) return 'overdue';
  return 'not-started';
};

// Helper function to get icon for timeline event
const getIconForTimelineEvent = (eventTitle: string) => {
  const title = eventTitle.toLowerCase();
  if (title.includes('start')) return 'play';
  if (title.includes('complete')) return 'check';
  if (title.includes('update')) return 'refresh-cw';
  if (title.includes('milestone')) return 'flag';
  if (title.includes('payment')) return 'dollar-sign';
  return 'circle';
};

const fetchBookingDetails = async (
  bookingId: string
): Promise<BookingDetail> => {
  if (!bookingId) {
    throw new Error('Booking ID is required');
  }

  const response = await api<{
    success: boolean;
    data: {
      booking_id: string;
      bid_id: string;
      job_id: string;
      bid_project: any;
      job: any;
      timeline: any[];
      deliverables: any[];
    };
  }>(`/bookings/${bookingId}`);

  const { data } = response;
  const jobData = data.job;
  const timelineData = data.timeline;
  const deliverables = data.deliverables;

  // Parse event date and times
  const eventDate = new Date(jobData.event_date);
  const startTime = new Date(jobData.event_date).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  });
  const endTime = new Date(
    new Date(jobData.event_date).getTime() + 2 * 60 * 60 * 1000
  ).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  });

  // Format timeline
  const formattedTimeline = (timelineData || []).map((event: any) => ({
    id: event.job_timeline_id,
    title: event.title,
    description: event.description || '',
    date: event.date, // Keep the date in ISO format
    icon: getIconForTimelineEvent(event.title),
    status: determineTimelineEventStatus(new Date(event.date)),
  }));

  // Format deliverables
  const formattedDeliverables = (deliverables || []).map((deliverable: any) => ({
    id: deliverable.job_deliverable_id,
    title: deliverable.title,
    description: deliverable.description || '',
    dueDate: new Date(deliverable.due_date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }),
    status: mapDeliverableStatus(deliverable.status, new Date(deliverable.due_date)),
  }));

  // Get client details
  const clientData = jobData.client_project?.client;
  const clientCompany = clientData?.company_name || 'Unknown Company';
  const clientUser = clientData?.user;
  const clientLogoUrl = clientData?.logo_url;

  return {
    id: bookingId,
    bidId: data.bid_id || null,
    bidProjectId: data.bid_project?.bid_project_id || null,
    projectId: jobData.client_project_id || null,
    title: jobData.title,
    date: eventDate.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }),
    startTime: startTime,
    endTime: endTime,
    location: jobData.location?.city_name || 'Unknown Location',
    client: {
      clientId: clientData?.client_id || '',
      name: clientUser ? `${clientUser.first_name} ${clientUser.last_name}` : 'Client',
      email: clientUser?.email || 'contact@example.com',
      phone: clientUser?.phone_number || 'N/A',
      company: clientCompany,
      companyDescription: clientData?.company_description,
      industry: clientData?.industry,
      logoUrl: clientLogoUrl,
      avatar: clientLogoUrl || '/placeholder.svg',
    },
    description: jobData.description || 'No description available',
    status: mapJobStatus(jobData.status, eventDate),
    timeline: formattedTimeline,
    deliverables: formattedDeliverables,
    budget: jobData.budget_amount,
    paymentMode: jobData.payment_mode || 'platform',
  };
};

export const useBookingDetails = (bookingId: string) => {
  return useQuery({
    queryKey: ['booking', bookingId],
    queryFn: () => fetchBookingDetails(bookingId),
    enabled: !!bookingId,
  });
};

// New hook to check payment status for a booking
export const useBookingPaymentStatus = (bookingId: string | undefined) => {
  return useQuery({
    queryKey: ['bookingPaymentStatus', bookingId],
    queryFn: async () => {
      if (!bookingId) return false;
      
      const response = await api<{
        success: boolean;
        data: { payment_status: boolean };
      }>(`/bookings/${bookingId}/payment-status`);
      
      return response.data.payment_status === true;
    },
    enabled: !!bookingId,
  });
};

// New hook to check deliverables completion status for a booking
export const useBookingDeliverablesStatus = (bookingId: string | undefined) => {
  return useQuery({
    queryKey: ['bookingDeliverablesStatus', bookingId],
    queryFn: async () => {
      if (!bookingId) return false;
      
      const response = await api<{
        success: boolean;
        data: { 
          deliverables: Array<{ status: string }>;
          all_approved: boolean;
        };
      }>(`/bookings/${bookingId}/deliverables-status`);
      
      return response.data.all_approved;
    },
    enabled: !!bookingId,
  });
};

// New hook to check booking type and status
export const useBookingStatus = (bookingId: string | undefined) => {
  return useQuery({
    queryKey: ['bookingStatus', bookingId],
    queryFn: async () => {
      if (!bookingId) return null;
      
      const response = await api<{
        success: boolean;
        data: {
          status: string;
          payment_status: boolean;
        };
      }>(`/bookings/${bookingId}/status`);
      
      return response.data;
    },
    enabled: !!bookingId,
  });
};